import React from 'react';
import { ArrowDown, Mail, Phone } from 'lucide-react';
import { profile, disciplines } from '../mock';

function MetaStrip() {
  return (
    <div className="pt-24 md:pt-28 px-6 md:px-10 max-w-[1500px] mx-auto">
      <div className="flex items-center justify-between gap-4">
        <a
          href={`mailto:${profile.email}`}
          className="hidden md:inline-block text-[11px] uppercase tracking-[0.32em] text-cream-soft/70 hover:text-orange-acc transition-colors"
        >
          {profile.email}
        </a>
        <span className="hidden md:inline text-[11px] uppercase tracking-[0.32em] text-cream-soft/70">
          Portfolio &mdash; {profile.year}
        </span>
        <div className="ml-auto md:ml-0 flex items-center gap-5">
          <a
            href={`tel:${profile.phone}`}
            className="hidden md:inline text-[11px] uppercase tracking-[0.32em] text-cream-soft/70 hover:text-orange-acc transition-colors"
          >
            {profile.phone}
          </a>
          <div className="relative w-14 h-14 md:w-16 md:h-16 rounded-full overflow-hidden ring-2 ring-orange-acc shadow-[0_8px_30px_rgba(232,120,46,0.25)] shrink-0">
            <img
              src="/portfolio/profile.jpg"
              alt={profile.name}
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function HeroBanner() {
  return (
    <div className="max-w-[1500px] mx-auto px-6 md:px-10 mt-8 md:mt-10">
      <img
        src="/portfolio/hero-banner.jpg"
        alt="PORTFOLIO — Shreya Chandrasekar — Interdisciplinary Design"
        className="block w-full h-auto select-none"
        draggable={false}
      />
    </div>
  );
}

function StripesAccent() {
  return (
    <div className="max-w-[1500px] mx-auto px-6 md:px-10 mt-6 md:mt-8">
      <div className="flex items-end gap-6 md:gap-10">
        <div className="flex-1 h-3 md:h-4 stripes-mask opacity-90" />
        <div className="shrink-0">
          <span className="font-display text-orange-acc text-3xl md:text-5xl tracking-wider">2025/26</span>
        </div>
      </div>
    </div>
  );
}

function IntroRow() {
  return (
    <div className="max-w-[1500px] mx-auto px-6 md:px-10 mt-16 md:mt-24 grid md:grid-cols-12 gap-10">
      <div className="md:col-span-7">
        <p className="text-[11px] uppercase tracking-[0.35em] text-orange-acc mb-4">Hello &mdash;</p>
        <h3 className="font-display text-4xl md:text-6xl leading-[1.05] text-cream">
          I&rsquo;m {profile.name.split(' ')[0]} &mdash; a designer learning to listen to every medium I touch.
        </h3>
        <p className="mt-6 max-w-[60ch] text-cream-soft/85 leading-relaxed">
          {profile.about}
        </p>
      </div>
      <div className="md:col-span-5 flex md:justify-end">
        <div className="flex flex-col gap-3 md:items-end text-cream-soft/90 text-sm">
          <span className="font-display text-cream text-xl tracking-wide">{profile.title}</span>
          <span>{profile.program}</span>
          <span className="opacity-70">{profile.university}</span>
          <div className="mt-4 flex items-center gap-4 text-cream">
            <a href={`mailto:${profile.email}`} className="hover:text-orange-acc inline-flex items-center gap-2">
              <Mail size={14} /> Email
            </a>
            <a href={`tel:${profile.phone}`} className="hover:text-orange-acc inline-flex items-center gap-2">
              <Phone size={14} /> Call
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function DisciplinesMarquee() {
  // duplicate the list for the seamless marquee loop; use stable composite keys
  const track = [
    ...disciplines.map((d) => ({ key: `a-${d}`, label: d })),
    ...disciplines.map((d) => ({ key: `b-${d}`, label: d })),
  ];
  return (
    <div className="mt-20 md:mt-28 border-y border-cream py-5 overflow-hidden">
      <div className="flex gap-12 marquee-track whitespace-nowrap">
        {track.map((item) => (
          <span key={item.key} className="font-display text-2xl md:text-4xl text-cream-soft/80 tracking-wider">
            {item.label} <span className="text-orange-acc">/</span>
          </span>
        ))}
      </div>
    </div>
  );
}

function ScrollCue() {
  return (
    <div className="max-w-[1500px] mx-auto px-6 md:px-10 mt-12 mb-12 flex items-center justify-between">
      <a href="#work" className="inline-flex items-center gap-3 text-cream hover:text-orange-acc text-sm uppercase tracking-[0.25em]">
        See the work <ArrowDown size={16} />
      </a>
      <span className="hidden md:block text-[11px] uppercase tracking-[0.3em] text-cream-soft/60">
        Scroll to explore
      </span>
    </div>
  );
}

export default function Hero() {
  return (
    <section id="top" className="relative min-h-screen overflow-hidden grain">
      <MetaStrip />
      <HeroBanner />
      <StripesAccent />
      <IntroRow />
      <DisciplinesMarquee />
      <ScrollCue />
    </section>
  );
}
